import React from 'react'

import * as c from '../../../../common'
import {
  TOTAL_PENDING_LABEL,
  totalCostPendingApply,
} from '../../../../common/helpers/totalCostDisplay'
import {
  MONITORING_MONTHLY,
  MonitoringChoice,
  appliedMonthlyPayment,
  effectiveMonthlyPayment,
  isSigningFlowV2,
  loanMonthlyFromSigned,
  monitoringChoiceOffered,
  monitoringOptionCopy,
  paymentSignReady,
  sanitizeMonitoringChoice,
} from '../../../../common/helpers/signingFlowV2'
import {
  cashHidesFinancing,
  isCashPayment,
} from '../../../../common/helpers/paymentDisplay'
import * as r from '../../../../react-utils'

import * as mi from '@mui/icons-material'
import Button from '@mui/material/Button'
import useMediaQuery from '@mui/material/useMediaQuery'
import { ThemeProvider, Theme, createTheme } from '@mui/material/styles'
import { useParams } from 'react-router-dom'

import * as cp from '../../../comps'
import * as cpm from '../../../../app/components'
import { productPhotoMap } from '../../../img/productPhotos/productPhotoMap'

import Cost, { ICost } from './Cost'
import NotesPage from './NotesPage'
import ProductViewer from './ProductViewer'
import {
  ViewerProduct,
  getViewerProducts,
  viewerProductKey,
} from './jobSummaryProducts'

//@ts-ignore
import styles from './Summary.module.css'

type NirvanaSummaryProps = c.r<{
  children?: never
  isPDF: boolean
  noPDF?: boolean
  hideSignButtons?: boolean
  readOnly?: boolean
  /** Signing v2: opens the Job Summary monthly payment signature dialog. */
  toggleSignPayment?(): void
  /** Signing v2: opens the Monthly Payment line signature dialog. */
  toggleSignMonthly?(): void
  /** Signing v2: opens the NOTES signature dialog. */
  toggleSignNotes?(): void
}>

/**
 * The monitoring charge, as a number rather than a number baked into a
 * sentence. It used to live only inside the string "Monitoring service:
 * $9.99/mo", which was fine while nothing added it up — now the comparison
 * below does, and a figure that appears twice must come from one place.
 *
 * Display-only, exactly as before: it never enters the Total (V46).
 *
 * Now imported from common/helpers/signingFlowV2 (V62): the monitoring
 * choice and the amount a payment signature freezes use the same figure.
 */

// V62: the canonical formatter lives in common/helpers.
const money = c.money

const photoFor = (type: string): string | undefined => productPhotoMap[type]

/**
 * Every product the Job Summary viewer steps through (Monday 13246279222), in
 * thumbnail order. The five detail fields are always read so the hook order
 * never changes; the PDF has no thumbnails and gets an empty list.
 */
const useViewerProducts = (
  customerID: string,
  costs: readonly ICost[],
  isPDF: boolean,
): readonly ViewerProduct[] => {
  const { rows: cams } = r.useRowable(customerID, 'security_cams_detail')
  const { rows: homeAutomation } = r.useRowable(
    customerID,
    'security_home_automation_detail',
  )
  const { rows: system } = r.useRowable(customerID, 'security_system_detail')
  const { rows: tv } = r.useRowable(customerID, 'security_tv_detail')
  const { rows: wifiCameras } = r.useRowable(
    customerID,
    'security_wifi_cameras_detail',
  )
  return React.useMemo(() => {
    if (isPDF) return []
    const rowsByField: Readonly<Partial<Record<string, readonly object[]>>> = {
      security_cams: cams,
      security_home_automation: homeAutomation,
      security_system: system,
      security_tv: tv,
      security_wifi_cameras: wifiCameras,
    }
    return getViewerProducts(
      costs.flatMap(({ name }) => {
        const rows = rowsByField[name]
        return rows ? [{ field: name, rows }] : []
      }),
      photoFor,
    )
  }, [cams, costs, homeAutomation, isPDF, system, tv, wifiCameras])
}

export default React.memo<NirvanaSummaryProps>(function NirvanaSummary({
  isPDF,
  noPDF,
  hideSignButtons,
  readOnly,
  toggleSignPayment,
  toggleSignMonthly,
  toggleSignNotes,
}) {
  const { customerID } = useParams<'customerID'>()
  if (!customerID) {
    throw new ReferenceError(
      `customerID route param not found in <NirvanaSummary />, instead found: ${customerID}`,
    )
  }
  //#region styling
  const nativePrefersDark = useMediaQuery('(prefers-color-scheme: dark)')
  const innerTheme = React.useMemo(
    (): Theme =>
      createTheme({
        palette: { mode: nativePrefersDark ? 'dark' : 'light' },
        breakpoints: { values: r.MUIBreakpoints },
      }),
    [nativePrefersDark],
  )
  //#endregion
  //#region global
  const [customerData] = r.useCustomer(customerID)
  // Jaron, Nov 1 (13443992736). See common/helpers/totalCostDisplay.ts.
  const totalPending = totalCostPendingApply(customerData, c.IS_MAIN)
  const { currProducts } = r.useProducts(customerID)
  const { appliedLoan, paymentData, mixedPricing, mixedAlarmPrices } =
    r.usePayment(customerID)
  const { contractCompanyShortName } = r.useContractCompany(customerID)
  const cash = isCashPayment(paymentData)

  // Same formula as FirstElectricContract.tsx/WildcatContract.tsx's Monthly
  // Payment line (T40) — Jaron (Slack #development-dpt, 2026-07-22): "This
  // section is wrong. Needs to have notes. Needs the monthly payment." The
  // Job Summary page (this component) was missing it even though the
  // contract pages further down already show it.
  // Formula moved to common/helpers (V62): the payment signature freezes the
  // same figure from CustomerReport.
  const calcMonthlyPayment = React.useCallback(
    (loan: c.PayMethodApplied | null): number => appliedMonthlyPayment(loan),
    [],
  )

  // The monthly the rep typed from the lender wins over our calculation
  // (Jaron, Oct 14: "I need the finance to match the rates exactly",
  // 13290658389). Same field as the M+S / CE contract's Monthly box.
  const [monthlyTyped, monthlyTypedWrite] = r.useCustomerFieldInput(
    customerID,
    'home_improvement_monthly_payment',
  )
  const monthlyCalculated = React.useMemo(
    (): number => calcMonthlyPayment(appliedLoan),
    [appliedLoan, calcMonthlyPayment],
  )
  const monthlyPayment = cash
    ? 0
    : effectiveMonthlyPayment(monthlyTyped, monthlyCalculated)

  // Luis (Slack, 2026-07-23): show it as an estimate before the rep has
  // locked the loan in via Payment Method — internal view only (!isPDF &&
  // !readOnly). The customer-facing contract/PDF/signing view must keep
  // requiring the officially applied loan (monthlyPayment above), unchanged.
  const estimatedMonthlyPayment = React.useMemo(
    (): number =>
      !isPDF && !readOnly && !appliedLoan && !(monthlyPayment > 0)
        ? calcMonthlyPayment(paymentData)
        : 0,
    [
      appliedLoan,
      calcMonthlyPayment,
      isPDF,
      monthlyPayment,
      paymentData,
      readOnly,
    ],
  )

  /**
   * Whichever monthly figure this page is actually showing — the applied loan
   * when there is one, the estimate otherwise.
   *
   * The monitoring comparison below compares against this rather than against
   * `monthlyPayment`, which is 0 until the rep locks the loan in. Inherits the
   * estimate's own internal-only rule (`estimatedMonthlyPayment` is already 0
   * on a PDF or a read-only view), so a customer-facing document still only
   * ever compares against an officially applied loan.
   */
  const monthlyBase = cash ? 0 : monthlyPayment || estimatedMonthlyPayment

  /*
   * Signing v2, phase B (tickets 13213629138, 13019071670, 13109586595). Only
   * for a customer on v2 or with no signature at all: a Job Summary signed
   * before keeps exactly its old layout and figures (V67).
   *
   * The customer picks WITH / SELF monitoring (not for M+S / CE, whose
   * customers never see monitoring, Maria G 2026-07-28) and signs the monthly
   * payment under the chosen option. The choice only changes the Monthly
   * Payment shown here; it never writes `security_monitoring_enabled` and
   * never touches any TOTAL (V46). Once signed, the amount shown is the one
   * frozen at signing (`job_summary_payment_signed_amount`) and the choice is
   * locked. Only with an applied loan: the Estimated line is internal-only.
   */
  const v2 = c.IS_MAIN && isSigningFlowV2(customerData)
  const choiceOffered = monitoringChoiceOffered(
    contractCompanyShortName,
    customerData,
  )
  const [choiceRaw, choiceWrite] = r.useCustomerField(
    customerID,
    'job_summary_monitoring_choice',
  )
  const choice = sanitizeMonitoringChoice(choiceRaw)
  const paymentSign = customerData.job_summary_payment_sign
  const paymentSigned = !!paymentSign
  const frozenMonthly = paymentSigned
    ? customerData.job_summary_payment_signed_amount
    : 0
  const showPaymentSign =
    v2 &&
    (!cash || choiceOffered) &&
    (cash || paymentSigned || monthlyPayment > 0)
  const showChoice = showPaymentSign && choiceOffered
  // The loan payment without monitoring, from the frozen figure once signed
  // (the signed amount includes the monitoring of the chosen option).
  const choiceBase =
    frozenMonthly > 0
      ? loanMonthlyFromSigned(frozenMonthly, choice, choiceOffered)
      : monthlyPayment
  // Oct 5 meeting (Luis notes): the Monthly Payment does not change with the
  // monitoring option; the option only shows its monitoring charge.
  const shownMonthly = !v2 ? monthlyPayment : choiceBase > 0 ? choiceBase : 0
  const choiceLocked = isPDF || paymentSigned
  // "Need to edit capability" (Jaron, Oct 14): the rep types the lender's
  // exact monthly right here until the customer signs it. Not on the PDF or
  // the customer's own signing link.
  const monthlyEditable = !cash && v2 && !isPDF && !readOnly && !paymentSigned
  const pickChoice = (value: MonitoringChoice) => {
    if (choiceLocked) return
    choiceWrite(value)
  }
  const signedStamp = (image: string, date: number) => (
    <div className={styles['sign-block']}>
      <img alt="" className={styles['sign-img']} src={image} />
      <p className={styles['sign-date']}>
        {c.formatContractDate(
          date,
          customerData.solarCompany,
          'MM/DD/YYYY - hh:mma',
        )}
      </p>
    </div>
  )
  // Kept in the DOM (display none) rather than not rendered: the guided
  // signing counts a step only while its button exists, so dropping it would
  // empty the Part 1 card and unlock Part 2.
  const hiddenUntilStart = hideSignButtons ? { display: 'none' } : undefined
  const paymentSignBlock = !showPaymentSign ? null : paymentSign ? (
    signedStamp(paymentSign, customerData.job_summary_payment_sign_date)
  ) : isPDF ? null : (
    <div className={styles['sign-block']} style={hiddenUntilStart}>
      <Button
        data-guided-signature="job_summary_payment_sign"
        disabled={!paymentSignReady(choice, choiceOffered)}
        onClick={toggleSignPayment}
        variant="contained"
      >
        {choiceOffered ? 'Sign monitoring option' : 'Sign monthly payment'}
      </Button>
      {!paymentSignReady(choice, choiceOffered) && (
        <p className={styles['sign-hint']}>Choose an option above to sign.</p>
      )}
    </div>
  )
  // Oct 5 meeting (Luis notes): two Job Summary signatures where the
  // monitoring choice is offered, one on the Monthly Payment line and one
  // under the chosen option. M+S / CE have no choice: their only signature
  // (`paymentSignBlock`) already sits on the Monthly Payment line.
  const monthlySign = customerData.job_summary_monthly_sign
  const monthlySignBlock =
    cash || !showChoice ? null : monthlySign ? (
      signedStamp(monthlySign, customerData.job_summary_monthly_sign_date)
    ) : isPDF ? null : (
      <div className={styles['sign-block']} style={hiddenUntilStart}>
        <Button
          data-guided-signature="job_summary_monthly_sign"
          onClick={toggleSignMonthly}
          variant="contained"
        >
          Sign monthly payment
        </Button>
      </div>
    )
  const notesSign = customerData.job_summary_notes_sign
  const notesSignBlock = !v2 ? null : notesSign ? (
    signedStamp(notesSign, customerData.job_summary_notes_sign_date)
  ) : isPDF ? null : (
    <div className={styles['sign-block']} style={hiddenUntilStart}>
      <Button
        data-guided-signature="job_summary_notes_sign"
        onClick={toggleSignNotes}
        variant="contained"
      >
        {/* Jaron, Oct 14: "Verify Equipment" instead of "Sign notes" */}
        Verify Equipment
      </Button>
    </div>
  )
  //#endregion
  //#region local
  const [closerCalcOpen, , toggleCloserCalc] = r.useBool(false)
  const [, , payMethodToggle] = r.useSessionVal('payMethodOpen')

  const [editingEfficiency, setEditingEfficiency] = React.useState('')
  const handleCloseEfficiencyEditor = React.useCallback(() => {
    setEditingEfficiency('')
  }, [setEditingEfficiency])
  const handleEfficiencyEdit = React.useCallback(
    (field: string) => {
      if (readOnly) return
      setEditingEfficiency(field)
    },
    [readOnly],
  )

  //#endregion

  // Jaron, Oct 16 (13312378676): the report shows the prices as they will be
  // once Payment Method's Apply runs, without having to press it. Display
  // only: mixed deals retain HI prices and distribute only the alarm loan;
  // single-family deals keep the original cost-weighted split. No writes.
  const previewGrandTotal =
    !appliedLoan &&
    paymentData != null &&
    typeof paymentData.grandTotalWDF === 'number' &&
    paymentData.grandTotalWDF > 0
      ? paymentData.grandTotalWDF
      : 0
  // Same company total Apply divides by (usePayment → useTotalCost)
  const companyTotalCost = r.useTotalCost('company', customerID, false)

  const costs = React.useMemo((): ICost[] => {
    const turnedOnFields = c.efficiencyKeys.filter(
      (field) => !field.startsWith('custom_') && customerData[field] === 'yes',
    ) as c.CustomerFields
    const filledCustomFields = c.getFilledCustomFields(customerData)

    const allActiveFields = [...turnedOnFields, ...filledCustomFields]

    return (
      allActiveFields
        .map((field) => {
          const customerCost = customerData[
            (field + '_customer_cost') as c.CustomerField
          ] as string
          const asApplied =
            mixedPricing && !appliedLoan && previewGrandTotal > 0
              ? c.isAlarmPricingKey(field)
                ? mixedAlarmPrices[field]?.toFixed(4) || ''
                : ''
              : previewGrandTotal > 0 && companyTotalCost > 0
              ? (
                  (previewGrandTotal *
                    c.parseFloating(
                      customerData[(field + '_cost') as c.CustomerField],
                    )) /
                  companyTotalCost
                ).toFixed(4)
              : ''
          return {
            customer: customerData,
            name: field,
            selected: customerData[(field + '_show') as c.CustomerField] === 1,
            value:
              asApplied && c.parseFloating(customerCost) > 0
                ? asApplied
                : customerCost,
          }
        })
        // A category the rep toggled to "Yes" but never added products to comes
        // through with no cost, and rendered as an empty $0 row (Jaron,
        // 2026-07-28). `useDetailLines` (Scope of Work) already drops empty
        // categories via its `rows.length === 0` checks — this keeps the Job
        // Summary consistent with it. A $0 row is always an unfinished
        // category, never a legitimately free item (confirmed with Samuel).
        .filter(({ value }) => c.parseFloating(value) > 0)
    )
  }, [
    appliedLoan,
    companyTotalCost,
    customerData,
    mixedAlarmPrices,
    mixedPricing,
    previewGrandTotal,
  ])

  // One viewer for the whole Job Summary, so it can step from product to
  // product (Monday 13246279222). The open product is kept even if a live
  // update drops it from the list: the viewer stays, without arrows.
  const viewerProducts = useViewerProducts(customerID, costs, isPDF)
  const [openProduct, setOpenProduct] = React.useState<ViewerProduct | null>(
    null,
  )
  const handleOpenProduct = React.useCallback(
    (field: string, type: string) =>
      setOpenProduct({
        key: viewerProductKey(field, type),
        photo: productPhotoMap[type] ?? '',
        type,
      }),
    [],
  )
  const handleCloseProduct = React.useCallback(() => setOpenProduct(null), [])
  const openIndex = openProduct
    ? viewerProducts.findIndex(({ key }) => key === openProduct.key)
    : -1
  const handlePrevProduct = React.useCallback(() => {
    const prev = viewerProducts[openIndex - 1]
    if (openIndex > 0 && prev) setOpenProduct(prev)
  }, [openIndex, viewerProducts])
  const handleNextProduct = React.useCallback(() => {
    const next = viewerProducts[openIndex + 1]
    if (openIndex >= 0 && next) setOpenProduct(next)
  }, [openIndex, viewerProducts])
  const viewerNav = React.useMemo(
    () =>
      openIndex >= 0 && viewerProducts.length > 1
        ? {
            index: openIndex,
            onNext: handleNextProduct,
            onPrev: handlePrevProduct,
            total: viewerProducts.length,
          }
        : undefined,
    [handleNextProduct, handlePrevProduct, openIndex, viewerProducts.length],
  )

  /*
   * UNSIGNED: the Total is the sum of the rows this table actually renders.
   * It used to come from `useTotalCost`, which counts an efficiency only when
   * `<field>_show === 1`, while the rows above are built from
   * `<field>_customer_cost > 0` and never look at `_show` at all — so a
   * customer whose `_show` fields were never written showed real line items
   * under a $0.00 Total (Samuel, QA on build 718: four security lines summing
   * $2,450 under a $0.00 Total). The two states are indistinguishable by the
   * time the data reaches here, because `sanitizeBit` turns both a missing
   * field and an explicit 0 into 0. Summing the rendered rows makes the Total
   * unable to disagree with what is on screen, and picks up the custom fields
   * the old path skipped entirely.
   *
   * SIGNED (MSE only): summing `_customer_cost` live is exactly what broke
   * Ciara Wilson's contract TOTAL (fixed in MSElectricContract.tsx/
   * MSElectricCertOfCompletion.tsx, 2026-09-03) — once
   * `has_touched_customer_cost` resets, `_customer_cost` re-links to the raw
   * catalog `cost` and the sum drifts below what the customer actually signed
   * for. This page never got that fix, so a rep opening a signed MSE
   * contract's Job Summary can still see a third number that agrees with
   * neither the contract nor the Certificate of Completion. Once signed, read
   * the same frozen `home_improvement_contract_price` those two documents
   * read, instead of recomputing.
   *
   * The `frozenContractPrice > 0` guard is load-bearing, not decorative: this
   * component renders for every company, but only MSElectricContract.tsx
   * ever writes `home_improvement_contract_price` — a signed Caballero's/FE/
   * HC/Wildcat/Other customer has it empty. Without the guard, "signed" alone
   * would zero out every one of their Job Summary Totals the moment they
   * sign. Falling back to the row sum keeps every non-MSE company exactly as
   * it behaves today.
   */
  const frozenContractPrice = c.parseFloating(
    customerData.home_improvement_contract_price,
  )
  const signed = !!(
    customerData.home_improvement_sign_owner1 ||
    customerData.home_improvement_sign_owner2 ||
    customerData.home_improvement_sign_company ||
    customerData.home_improvement_confirm_equipment_sign ||
    customerData.home_improvement_confirm_total_initials ||
    customerData.home_improvement_financing_info_sign
  )
  const totalCustomerCost = React.useMemo(
    () =>
      signed && frozenContractPrice > 0
        ? frozenContractPrice
        : costs.reduce((acc, { value }) => acc + c.parseFloating(value), 0),
    [costs, frozenContractPrice, signed],
  )

  // Jaron, Oct 25 (13386542071): each option explains monitoring and shows
  // its final cost again. Financing: the loan monthly (frozen once signed,
  // `choiceBase`) plus that option's monitoring. Cash (no loan monthly): the
  // Total, plus $9.99/mo only with monitoring. A Job Summary signed with a
  // loan monthly keeps showing it even if the method changed afterwards.
  const monitoringOptions = monitoringOptionCopy({
    loanMonthly: paymentSigned || !cash ? choiceBase : 0,
    total: totalCustomerCost,
    totalLabel: totalPending ? TOTAL_PENDING_LABEL : undefined,
    money,
  })
  // Rep-only nudge (13386542071): Cash hides the Monthly Payment, so a Cash
  // method with a lender / term / typed monthly left over is most likely a
  // deal that should be on Financing. Never on the PDF or the customer view.
  const showCashHint =
    !isPDF && !readOnly && cashHidesFinancing(paymentData, monthlyTyped)

  // Jaron (Monday 13019053126, 2026-09-10): the Total bar used the
  // per-company brand color (orange for NPS in his screenshot) — replace it
  // with a fixed neutral gray, same shade already used for the "inactive"
  // state elsewhere in the report (FinancingSection.tsx, LoanOptions.tsx).
  // Scoped to this bar only — the shared PageReport company-colored border
  // stripes are a separate, wider-blast-radius change (see ticket note).
  const colorTotalStyle: React.CSSProperties = { backgroundColor: '#6C757D' }

  return (
    <>
      <cp.PageReport
        autoHeight
        company={customerData.solarCompany}
        companyBorder
        hPad
        noPDF={noPDF}
        title="Job Summary"
      >
        <table className={styles['table-root']}>
          {/* Keep the editor columns aligned whether or not the header renders. */}
          <colgroup>
            {!isPDF && <col style={{ width: 28 }} />}
            <col style={{ width: 130 }} />
            {!isPDF && !readOnly && <col style={{ width: 90 }} />}
            {/* Short notes, back in the Job Summary (13213613154). The only
                column without a width: it takes what the others leave. */}
            <col />
            {!isPDF && <col style={{ width: 80 }} />}
            {!isPDF && <col style={{ width: 40 }} />}
          </colgroup>
          {!isPDF && (
            <thead>
              <tr className={styles['table-header-row']}>
                <th style={{ width: 28 }}></th>
                <th style={{ textAlign: 'left', paddingLeft: 10, width: 130 }}>
                  Category
                </th>
                {!readOnly && <th style={{ width: 90 }}>Price</th>}
                <th style={{ textAlign: 'left', paddingLeft: 8 }}>Notes</th>
                <th style={{ width: 80 }}>Photos</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
          )}
          <tbody>
            {costs.map((cost) => (
              <Cost
                customer={customerData}
                customerID={customerID}
                isPDF={isPDF}
                key={cost.name}
                name={cost.name}
                onEfficiencyEdit={handleEfficiencyEdit}
                onOpenProduct={handleOpenProduct}
                readOnly={readOnly}
                selected={cost.selected}
                value={cost.value}
              />
            ))}
          </tbody>
        </table>
        {openProduct && (
          <ProductViewer
            nav={viewerNav}
            onClose={handleCloseProduct}
            photo={openProduct.photo}
            productKey={openProduct.key}
            type={openProduct.type}
          />
        )}

        <div className={styles['check-total-cost']} style={colorTotalStyle}>
          <span className={styles['check-total-cost-label']}>
            <span>Total:</span>
          </span>
          <span className={styles['check-total-cost-price']}>
            {/* `money()` rather than a bare `.toFixed(2)`. This is the biggest
                number on the Job Summary and it was the only amount on the
                page without a thousands separator: every category above it
                reads "$19,500" and "$15,200", the Monthly Payment right below
                formats through Intl, and the Total read "$43295.00". */}
            <span>
              {totalPending ? TOTAL_PENDING_LABEL : money(totalCustomerCost)}
            </span>
          </span>
        </div>

        {!cash && (shownMonthly > 0 || (monthlyEditable && !!appliedLoan)) && (
          <div className={styles['monthly-payment']}>
            <span className={styles['check-total-cost-label']}>
              <span>Monthly Payment:</span>
            </span>
            <span className={styles['check-total-cost-price']}>
              {monthlyEditable ? (
                <span>
                  $
                  <input
                    aria-label="Monthly payment from the lender"
                    className={styles['monthly-input']}
                    inputMode="decimal"
                    onChange={(e): void =>
                      // No minus sign: parseMonthlyInput drops it, so "-50" was
                      // shown as -50 but signed as $50.
                      monthlyTypedWrite(e.target.value.replace(/-/g, ''))
                    }
                    value={
                      monthlyTyped ||
                      (monthlyCalculated > 0
                        ? monthlyCalculated.toFixed(2)
                        : '')
                    }
                  />
                  /mo
                </span>
              ) : (
                <span>
                  {Intl.NumberFormat('en-US', {
                    currency: 'USD',
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                    style: 'currency',
                  }).format(shownMonthly)}
                  /mo
                </span>
              )}
            </span>
          </div>
        )}

        {showCashHint && (
          <div className={styles['cash-hint']} role="note">
            Payment method is Cash, so the Monthly Payment is hidden. Switch to
            Financing in Payment Method to show the monthly payment.
          </div>
        )}

        {/* M+S / CE: no monitoring choice, the signature goes right under the
            monthly payment. */}
        {showPaymentSign && !choiceOffered && paymentSignBlock}
        {monthlySignBlock}

        {!cash && estimatedMonthlyPayment > 0 && (
          <div className={styles['monthly-payment']}>
            <span className={styles['check-total-cost-label']}>
              <span>Estimated Monthly Payment:</span>
            </span>
            <span className={styles['check-total-cost-price']}>
              <span>
                {Intl.NumberFormat('en-US', {
                  currency: 'USD',
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                  style: 'currency',
                }).format(estimatedMonthlyPayment)}
                /mo
              </span>
            </span>
          </div>
        )}

        {/* Jaron asked for the monitoring line here too (2026-07-28) — the 3
            contract templates already carry it (V46), the Job Summary didn't.
            Wording kept identical to them on purpose so the two sections of
            the same report can't disagree. Independent of monthlyPayment, so a
            cash customer with monitoring still sees it (same as V46's rule for
            Payment.tsx). Display-only: never enters the Total. */}
        {/* Maria G (Slack, 2026-07-28): CE (Caballero's Electric) customers
            must never see the monitoring line on their PDF - "We can't let
            them know or have them see that there's an alarm monitoring
            service included." Toggle itself untouched - reps still see this
            in the live/internal view regardless of company, only hidden
            from CE's exported PDF. Extended to MSE 2026-07-30 (Maria G,
            confirmed via Luis: MS Electric gets "todo lo de las alarmas"
            same as Caballero's). */}
        {/* No longer gated on security_monitoring_enabled (Jaron,
            #development-dpt 2026-09-11: "I want this section to be up
            whether or not monitoring is selected"). The block always
            renders now; the CE/MSE PDF-hide rule above is unrelated and
            stays as-is. Labels already matched his clarification before this
            change - "With monitoring" adds MONITORING_MONTHLY (=Yes), "Self
            monitoring" adds 0 (=No) - so no relabeling was needed. */}
        {/* M+S shows it since Oct 14 (Jaron: the customer chooses and signs
            here), so only CE, and an M+S Job Summary signed before that,
            still hide it on the PDF. */}
        {!(
          isPDF &&
          (contractCompanyShortName === 'CE' ||
            (contractCompanyShortName === 'MSE' && !choiceOffered))
        ) && (
          <>
            <p className={styles['monitoring-disclaimer']}>
              Monitoring service: {money(MONITORING_MONTHLY)}/mo (billed
              monthly, not included in total)
            </p>

            {/* Jaron, 2026-08-26: "I want in this section to show the total
                  calculation with or without monitoring depending on what
                  people choose", with both sums written out by hand on the
                  screenshot — $70.00 + $9.99 = $79.99 against $70.00 + $0.00.
                  The line above already stated the charge; what he was missing
                  is that the customer had to do the addition themselves.

                  Compares against whichever monthly figure this page is
                  already showing. Gating it on `monthlyPayment` alone was
                  wrong and was caught in the browser, not in review: that one
                  is 0 until the rep locks the loan in through Payment Method,
                  and until then the page shows `estimatedMonthlyPayment`
                  instead. So the block vanished in exactly the situation
                  Jaron wants it for — the sales presentation, before anything
                  is signed. Verified on Luis Test Customer, which shows
                  "Estimated Monthly Payment: $36.63/mo" and no applied loan.

                  The estimate is already internal-only (`!isPDF && !readOnly`,
                  Luis 2026-07-23), so following it here inherits that rule
                  rather than inventing one: a customer-facing PDF still only
                  ever compares against an officially applied loan.

                  Still nothing to compare for a cash customer, who has no
                  monthly figure at all. And still inside the guard above, so
                  CE and MSE exported PDFs show none of it (Maria G,
                  2026-07-28). */}
            {/* Signing v2 (13019071670 / 13109586595, Jaron: "just give
                letters, needs to be aligned with total amount"): two plain
                clickable lines laid out like the Total bar (label left,
                amount right), replacing the comparison cards below. The
                payment signature sits under the chosen one. Since
                13386542071 (Jaron, Oct 25) each line shows its final cost
                again, with the breakdown under it (`monitoringOptions`). */}
            {showChoice && (
              <div
                aria-label="Monitoring"
                className={styles['monitoring-choice']}
                role="radiogroup"
              >
                {monitoringOptions.map(({ value, label, final, breakdown }) => (
                  <div key={value}>
                    <button
                      aria-checked={choice === value}
                      className={
                        styles['monitoring-choice-row'] +
                        (choice === value
                          ? ' ' + styles['monitoring-choice-row-selected']
                          : '')
                      }
                      disabled={choiceLocked}
                      onClick={() => pickChoice(value)}
                      role="radio"
                      type="button"
                    >
                      <span className={styles['monitoring-choice-label']}>
                        <span
                          aria-hidden
                          className={
                            styles['monitoring-choice-dot'] +
                            (choice === value
                              ? ' ' + styles['monitoring-choice-dot-on']
                              : '')
                          }
                        />
                        {label}
                      </span>
                      <span className={styles['monitoring-choice-amount']}>
                        {final}
                      </span>
                      <span className={styles['monitoring-choice-breakdown']}>
                        {breakdown}
                      </span>
                    </button>
                    {choice === value && paymentSignBlock}
                  </div>
                ))}
                {!choice && paymentSignBlock}
              </div>
            )}

            {!showChoice && monthlyBase > 0 && (
              <div className={styles['monitoring-compare']}>
                {[
                  { label: 'With monitoring', extra: MONITORING_MONTHLY },
                  { label: 'Self monitoring', extra: 0 },
                ].map(({ label, extra }) => (
                  <div key={label} className={styles['monitoring-option']}>
                    <span className={styles['monitoring-option-label']}>
                      {label}
                    </span>
                    <span>Monthly payment: {money(monthlyBase)}</span>
                    <span>Monitoring: {money(extra)}</span>
                    <span className={styles['monitoring-option-total']}>
                      Total monthly: {money(monthlyBase + extra)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {!isPDF && !readOnly && (
          <div className={styles['calc-buttons']}>
            {(currProducts.includes('Solar') || currProducts.length === 0) &&
              c.CODENAME !== 'nirvana' && (
                <>
                  {/* Dunno even why */}
                  {/* @ts-expect-error */}
                  <mi.Calculate
                    //
                    onClick={toggleCloserCalc}
                    size={24}
                  />
                </>
              )}

            {(currProducts.includes('HIL') ||
              currProducts.includes('Security') ||
              currProducts.length === 0) && (
              <>
                {/* Dunno even why */}
                {/* @ts-expect-error */}
                <mi.CandlestickChart
                  //
                  onClick={payMethodToggle}
                  size={24}
                />
              </>
            )}
          </div>
        )}

        <cp.Pad amt={20} />
        <p className={styles['efficiency-note']}>
          This is an agreement that all work described on this report will be
          completed.
        </p>
        <p className={styles['efficiency-note']}>
          Customer understands the timeline in which jobs will be completed as
          explained by the representative.
        </p>
      </cp.PageReport>

      <NotesPage
        customerData={customerData}
        costs={costs}
        noPDF={noPDF}
        signature={notesSignBlock}
      />

      <ThemeProvider theme={innerTheme}>
        <cp.Dialog
          noCanvas
          onClose={handleCloseEfficiencyEditor}
          open={!!editingEfficiency && !readOnly}
        >
          {!!editingEfficiency && !readOnly && (
            <cpm.CheckGroup
              as="canvas"
              customerID={customerID}
              field={editingEfficiency as c.CustomerField}
            />
          )}
        </cp.Dialog>
      </ThemeProvider>

      <ThemeProvider theme={innerTheme}>
        <cp.Dialog onClose={toggleCloserCalc} open={closerCalcOpen}>
          <cpm.CloserCalc customerID={customerID} deductDisabled />
        </cp.Dialog>
      </ThemeProvider>
    </>
  )
})
